/**
 * Ficha/editor de presupuesto. Cubre /quotes/new y /quotes/:id.
 *
 * En BORRADOR todo es editable (cliente, oportunidad, fechas, líneas, IRPF).
 * En el resto de estados se muestra en solo lectura con las acciones que
 * correspondan: PDF, enviar por email, aceptar/rechazar, convertir a factura.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertCircle,
  CalendarClock,
  Check,
  ChevronLeft,
  Coins,
  FileDown,
  FileSignature,
  Loader2,
  Mail,
  Plus,
  Receipt,
  Save,
  Send,
  Trash2,
  X,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { confirmDialog } from '../lib/dialogs'
import { SELECTOR_PAGE_SIZE } from '../lib/constants'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../lib/money'
import Modal from '../components/ui/Modal'
import RatePickerModal from '../components/rates/RatePickerModal'
import MilestonesPanel, { PaymentPlanModal } from '../components/billing/MilestonesPanel'
import { QuoteStatusBadge } from './Quotes'
import {
  type OpportunityListItem,
  type ClientListResponse,
  type ProductDto,
  type QuoteDetail,
  type QuoteLineInput,
} from '@toga/shared'

interface LineDraft {
  key: number
  productId: string | null
  description: string
  quantity: string
  unitPriceEur: string
  costEur: string // coste interno por unidad — nunca llega al cliente
  discountPctInput: string // "10" = 10 %
  vatPctInput: string // "21" = 21 %
  isSupplied: boolean
}

let lineKeySeq = 1

function emptyLine(): LineDraft {
  return {
    key: lineKeySeq++,
    productId: null,
    description: '',
    quantity: '1',
    unitPriceEur: '',
    costEur: '',
    discountPctInput: '0',
    vatPctInput: '21',
    isSupplied: false,
  }
}

function lineToDraft(l: QuoteDetail['lines'][number]): LineDraft {
  return {
    key: lineKeySeq++,
    productId: l.productId,
    description: l.description,
    quantity: l.quantity,
    unitPriceEur: centsToEurosInput(l.unitPriceCents),
    costEur: centsToEurosInput(l.costCents ?? 0),
    discountPctInput: (Number(l.discountPct) * 100).toString(),
    vatPctInput: (Number(l.vatRate) * 100).toString(),
    isSupplied: l.isSupplied,
  }
}

function draftToInput(l: LineDraft, position: number): QuoteLineInput {
  return {
    position,
    productId: l.productId,
    description: l.description.trim(),
    quantity: l.quantity || '1',
    unitPriceCents: parseEurosToCents(l.unitPriceEur),
    costCents: parseEurosToCents(l.costEur),
    discountPct: String((Number(l.discountPctInput) || 0) / 100),
    vatRate: String((Number(l.vatPctInput) || 0) / 100),
    isSupplied: l.isSupplied,
  }
}

function computeDraftTotals(lines: LineDraft[], irpfPct: number) {
  let subtotal = 0
  let vat = 0
  let irpfBase = 0
  let totalCost = 0
  for (const l of lines) {
    const qty = Number(l.quantity) || 0
    const unit = parseEurosToCents(l.unitPriceEur)
    const disc = (Number(l.discountPctInput) || 0) / 100
    const net = Math.round(qty * unit * (1 - disc))
    subtotal += net
    totalCost += Math.round(qty * parseEurosToCents(l.costEur))
    if (!l.isSupplied) {
      vat += Math.round((net * (Number(l.vatPctInput) || 0)) / 100)
      irpfBase += net
    }
  }
  const irpf = Math.round((irpfBase * irpfPct) / 100)
  return {
    subtotal,
    vat,
    irpf,
    total: subtotal + vat - irpf,
    totalCost,
    margin: subtotal - totalCost,
  }
}

const inputCls =
  'w-full px-2.5 py-1.5 rounded-lg bg-[var(--bg-input-soft)] border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50 disabled:opacity-60'

export default function QuoteEditorPage() {
  const { t } = useTranslation('quotes')
  const { id } = useParams<{ id: string }>()
  const isNew = !id
  const navigate = useNavigate()

  const [detail, setDetail] = useState<QuoteDetail | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionBusy, setActionBusy] = useState<string | null>(null)

  // Form state
  const [clients, setClients] = useState<Array<{ id: string; displayName: string; email: string | null }>>([])
  const [cases, setCases] = useState<OpportunityListItem[]>([])
  const [products, setProducts] = useState<ProductDto[]>([])
  const [clientId, setClientId] = useState('')
  const [opportunityId, setCaseId] = useState('')
  const [title, setTitle] = useState('')
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [validUntil, setValidUntil] = useState('')
  const [irpfPctInput, setIrpfPctInput] = useState('0')
  const [notes, setNotes] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()])

  const [ratePickerOpen, setRatePickerOpen] = useState(false)
  const [planOpen, setPlanOpen] = useState(false)
  const [milestonesKey, setMilestonesKey] = useState(0)
  const [sendOpen, setSendOpen] = useState(false)
  const [sendTo, setSendTo] = useState('')
  const [sendMessage, setSendMessage] = useState('')

  const editable = isNew || detail?.status === 'BORRADOR'

  // Clientes para el selector
  useEffect(() => {
    api
      .get<ClientListResponse>(`/clients?pageSize=${SELECTOR_PAGE_SIZE}&sortBy=name&sortDir=asc`)
      .then((r) =>
        setClients(
          r.items.map((c) => ({ id: c.id, displayName: c.displayName, email: c.email ?? null }))
        )
      )
      .catch(() => setClients([]))
  }, [])

  // F35 — Productos/servicios del catálogo (solo activos), una vez al montar.
  useEffect(() => {
    let cancelled = false
    api
      .get<{ items: ProductDto[] }>('/products')
      .then((r) => {
        if (cancelled) return
        const sorted = [...r.items].sort(
          (a, b) => a.position - b.position || a.name.localeCompare(b.name)
        )
        setProducts(sorted)
      })
      .catch(() => {
        if (!cancelled) setProducts([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Oportunidades del cliente seleccionado
  useEffect(() => {
    if (!clientId) {
      setCases([])
      return
    }
    api
      .get<{ items: OpportunityListItem[] }>(`/opportunities?clientId=${clientId}&pageSize=${SELECTOR_PAGE_SIZE}`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
  }, [clientId])

  const applyDetail = useCallback((q: QuoteDetail) => {
    setDetail(q)
    setClientId(q.clientId)
    setCaseId(q.opportunityId ?? '')
    setTitle(q.title ?? '')
    setIssueDate(q.issueDate.slice(0, 10))
    setValidUntil(q.validUntil ? q.validUntil.slice(0, 10) : '')
    setIrpfPctInput((Number(q.irpfRate) * 100).toString())
    setNotes(q.notes ?? '')
    setInternalNotes(q.internalNotes ?? '')
    setLines(q.lines.length > 0 ? q.lines.map(lineToDraft) : [emptyLine()])
    setSendTo(q.sentTo ?? '')
  }, [])

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const { quote } = await api.get<{ quote: QuoteDetail }>(`/quotes/${id}`)
      applyDetail(quote)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.load'))
    } finally {
      setLoading(false)
    }
  }, [id, applyDetail])

  useEffect(() => {
    void load()
  }, [load])

  const totals = useMemo(
    () => computeDraftTotals(lines, Number(irpfPctInput) || 0),
    [lines, irpfPctInput]
  )

  const updLine = (key: number, patch: Partial<LineDraft>) => {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }

  // F35 — Al elegir un producto del catálogo, autorellenamos descripción/precio/IVA
  // y guardamos el productId. El usuario puede luego sobrescribir cualquier campo.
  const onPickProduct = (key: number, productId: string) => {
    if (!productId) {
      updLine(key, { productId: null })
      return
    }
    const p = products.find((x) => x.id === productId)
    if (!p) return
    updLine(key, {
      productId: p.id,
      description: p.name,
      unitPriceEur: centsToEurosInput(p.unitPriceCents),
      vatPctInput: (p.vatRateBps / 100).toString(),
    })
  }

  const validLines = lines.filter((l) => l.description.trim() && parseEurosToCents(l.unitPriceEur) !== 0)

  const onSave = async () => {
    if (!clientId) {
      setError(t('errors.selectClient'))
      return
    }
    if (validLines.length === 0) {
      setError(t('errors.addLine'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        clientId,
        opportunityId: opportunityId || null,
        title: title.trim() || null,
        notes: notes.trim() || null,
        internalNotes: internalNotes.trim() || null,
        issueDate: new Date(`${issueDate}T12:00:00`).toISOString(),
        validUntil: validUntil ? new Date(`${validUntil}T23:59:59`).toISOString() : null,
        irpfRate: String((Number(irpfPctInput) || 0) / 100),
        lines: validLines.map((l, i) => draftToInput(l, i)),
      }
      if (isNew) {
        const { quote } = await api.post<{ quote: QuoteDetail }>('/quotes', payload)
        navigate(`/quotes/${quote.id}`, { replace: true })
        applyDetail(quote)
      } else {
        const { quote } = await api.patch<{ quote: QuoteDetail }>(`/quotes/${id}`, payload)
        applyDetail(quote)
      }
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('errors.save')))
    } finally {
      setSaving(false)
    }
  }

  const runAction = async (key: string, fn: () => Promise<void>) => {
    setActionBusy(key)
    setError(null)
    try {
      await fn()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('errors.generic')))
    } finally {
      setActionBusy(null)
    }
  }

  const onTransition = (action: 'ACEPTAR' | 'RECHAZAR' | 'MARCAR_ENVIADO') =>
    runAction(action, async () => {
      const { quote } = await api.post<{ quote: QuoteDetail }>(`/quotes/${id}/transition`, {
        action,
      })
      applyDetail(quote)
    })

  const onSend = () =>
    runAction('SEND', async () => {
      const { quote } = await api.post<{ quote: QuoteDetail }>(`/quotes/${id}/send`, {
        ...(sendTo.trim() ? { to: sendTo.trim() } : {}),
        ...(sendMessage.trim() ? { message: sendMessage.trim() } : {}),
      })
      applyDetail(quote)
      setSendOpen(false)
    })

  const onToInvoice = () =>
    runAction('INVOICE', async () => {
      const { invoiceId } = await api.post<{ invoiceId: string }>(`/quotes/${id}/to-invoice`)
      navigate(`/billing/${invoiceId}`)
    })

  const onDelete = () =>
    runAction('DELETE', async () => {
      if (!(await confirmDialog({ message: t('confirmDelete'), tone: 'danger' }))) return
      await api.delete(`/quotes/${id}`)
      navigate('/quotes')
    })

  const clientEmail = clients.find((c) => c.id === clientId)?.email ?? null
  const selectedCase = cases.find((c) => c.id === opportunityId)

  if (loading) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> {t('loading')}
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-slideUp max-w-5xl">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/quotes" className="t-muted hover:text-toga-700 dark:hover:text-toga-200">
            <ChevronLeft size={18} />
          </Link>
          <div>
            <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
              <FileSignature size={20} className="text-toga-700 dark:text-toga-300" />
              {isNew ? t('newQuote') : detail?.number}
              {detail && <QuoteStatusBadge status={detail.status} />}
            </h1>
            {detail?.sentAt && (
              <p className="text-xs t-muted">
                {t('sentOn', { date: new Date(detail.sentAt).toLocaleString(currentLocaleTag()) })}
                {detail.sentTo ? t('sentTo', { to: detail.sentTo }) : ''}
              </p>
            )}
            {detail?.invoiceId && (
              <p className="text-xs t-muted">
                {t('invoiceLabel')}{' '}
                <Link to={`/billing/${detail.invoiceId}`} className="text-toga-700 dark:text-toga-300 hover:underline">
                  {detail.invoiceNumber ?? t('draft')}
                </Link>
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && (
            <button
              type="button"
              onClick={() => window.open(`/api/quotes/${id}/pdf`, '_blank')}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <FileDown size={14} /> PDF
            </button>
          )}
          {!isNew && detail && detail.status !== 'FACTURADO' && (
            <button
              type="button"
              onClick={() => {
                setSendTo(detail.sentTo ?? clientEmail ?? '')
                setSendOpen(true)
              }}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
            >
              <Mail size={14} /> {t('sendByEmail')}
            </button>
          )}
          {detail?.status === 'BORRADOR' && (
            <button
              type="button"
              disabled={actionBusy !== null}
              onClick={() => void onTransition('MARCAR_ENVIADO')}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
              title={t('markSentTitle')}
            >
              <Send size={14} /> {t('markSent')}
            </button>
          )}
          {detail && ['BORRADOR', 'ENVIADO', 'EN_NEGOCIACION', 'CADUCADO'].includes(detail.status) && (
            <>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => void onTransition('ACEPTAR')}
                className="px-3 py-1.5 rounded-lg text-sm border border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1.5"
              >
                <Check size={14} /> {t('accept')}
              </button>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => void onTransition('RECHAZAR')}
                className="px-3 py-1.5 rounded-lg text-sm border border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10 flex items-center gap-1.5"
              >
                <X size={14} /> {t('reject')}
              </button>
            </>
          )}
          {detail?.status === 'ACEPTADO' && (
            <>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => setPlanOpen(true)}
                className="px-3 py-1.5 rounded-lg text-sm border border-teal-500/40 text-teal-700 dark:text-teal-300 hover:bg-teal-500/10 flex items-center gap-1.5"
                title={t('paymentPlanTitle')}
              >
                <CalendarClock size={14} /> {t('paymentPlan')}
              </button>
              <button
                type="button"
                disabled={actionBusy !== null}
                onClick={() => void onToInvoice()}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
                title={t('toInvoiceTitle')}
              >
                {actionBusy === 'INVOICE' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Receipt size={14} />
                )}
                {t('toInvoice')}
              </button>
            </>
          )}
          {editable && (
            <button
              type="button"
              disabled={saving}
              onClick={() => void onSave()}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t('save')}
            </button>
          )}
          {!isNew && detail && detail.status !== 'FACTURADO' && (
            <button
              type="button"
              disabled={actionBusy !== null}
              onClick={() => void onDelete()}
              className="w-8 h-8 grid place-items-center rounded-lg border border-base hover:border-rose-500/40 hover:text-rose-700 dark:hover:text-rose-300 t-muted"
              title={t('deleteTitle')}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Datos generales */}
      <div className="surface p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="col-span-2">
          <label className="block text-xs t-muted mb-1">{t('client')}</label>
          <select
            disabled={!editable}
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value)
              setCaseId('')
            }}
            className={inputCls}
          >
            <option value="">{t('selectClient')}</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs t-muted mb-1">{t('opportunity')}</label>
          <select
            disabled={!editable || !clientId}
            value={opportunityId}
            onChange={(e) => setCaseId(e.target.value)}
            className={inputCls}
          >
            <option value="">{t('noOpportunity')}</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.title}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2 md:col-span-4">
          <label className="block text-xs t-muted mb-1">{t('concept')}</label>
          <input
            disabled={!editable}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('conceptPlaceholder')}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('date')}</label>
          <input
            type="date"
            disabled={!editable}
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('validUntil')}</label>
          <input
            type="date"
            disabled={!editable}
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('irpfLabel')}</label>
          <input
            type="number"
            min={0}
            max={47}
            step="0.01"
            disabled={!editable}
            value={irpfPctInput}
            onChange={(e) => setIrpfPctInput(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* Líneas */}
      <div className="surface p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="t-primary font-semibold text-sm">{t('lines')}</h3>
          {editable && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!clientId}
                onClick={() => setRatePickerOpen(true)}
                className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5 disabled:opacity-40"
                title={clientId ? t('fromRateTitle') : t('selectClientFirst')}
              >
                <Coins size={13} /> {t('fromRate')}
              </button>
              <button
                type="button"
                onClick={() => setLines((ls) => [...ls, emptyLine()])}
                className="px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5"
              >
                <Plus size={13} /> {t('addLine')}
              </button>
            </div>
          )}
        </div>

        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="text-left py-1">{t('col.description')}</th>
              <th className="text-right w-20">{t('col.qty')}</th>
              <th className="text-right w-28">{t('col.price')}</th>
              <th
                className="text-right w-28 text-amber-400/70"
                title={t('col.costTitle')}
              >
                {t('col.cost')} <span className="normal-case text-[9px]">{t('col.int')}</span>
              </th>
              <th
                className="text-right w-24 text-amber-400/70"
                title={t('col.marginTitle')}
              >
                {t('col.margin')}
              </th>
              <th className="text-right w-20">{t('col.discount')}</th>
              <th className="text-right w-20">{t('col.vat')}</th>
              <th className="text-center w-16" title={t('col.suppliedTitle')}>{t('col.supplied')}</th>
              <th className="text-right w-28">{t('col.total')}</th>
              {editable && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => {
              const qty = Number(l.quantity) || 0
              const unit = parseEurosToCents(l.unitPriceEur)
              const disc = (Number(l.discountPctInput) || 0) / 100
              const net = Math.round(qty * unit * (1 - disc))
              const vat = l.isSupplied ? 0 : Math.round((net * (Number(l.vatPctInput) || 0)) / 100)
              const lineMargin = net - Math.round(qty * parseEurosToCents(l.costEur))
              return (
                <tr key={l.key} className="border-t border-base/40">
                  <td className="py-1.5 pr-2">
                    {/* Misma fila que en Pagos recurrentes: el selector del
                        catálogo, compacto y sin rótulo, al lado de la
                        descripción. La versión apilada con etiqueta y texto de
                        ayuda ocupaba tres alturas por línea y hacía la tabla
                        ilegible con más de dos conceptos. */}
                    <div className="flex items-center gap-1">
                      {products.length > 0 && (
                        <select
                          disabled={!editable}
                          value={l.productId ?? ''}
                          onChange={(e) => onPickProduct(l.key, e.target.value)}
                          className={`${inputCls} w-36 shrink-0`}
                          title={t('catalogProductTitle')}
                        >
                          <option value="">{t('manual')}</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.code} · {p.name}
                            </option>
                          ))}
                        </select>
                      )}
                      <input
                        disabled={!editable}
                        value={l.description}
                        onChange={(e) => updLine(l.key, { description: e.target.value })}
                        placeholder={t('servicePlaceholder')}
                        className={`${inputCls} flex-1`}
                      />
                    </div>
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.quantity}
                      onChange={(e) => updLine(l.key, { quantity: e.target.value })}
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.unitPriceEur}
                      onChange={(e) => updLine(l.key, { unitPriceEur: e.target.value })}
                      placeholder="0,00"
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.costEur}
                      onChange={(e) => updLine(l.key, { costEur: e.target.value })}
                      placeholder="0,00"
                      title={t('internalCostTitle')}
                      className="w-full px-2.5 py-1.5 rounded-lg bg-amber-500/[0.04] border border-amber-500/20 text-sm text-amber-800 dark:text-amber-200/90 text-right placeholder:text-amber-700 dark:text-amber-300/30 focus:outline-none focus:border-amber-500/40 disabled:opacity-60"
                    />
                  </td>
                  <td
                    className="py-1.5 pr-2 text-right font-mono text-xs text-amber-700 dark:text-amber-300/70"
                    title={t('lineMarginTitle')}
                  >
                    {formatCents(lineMargin)}
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.discountPctInput}
                      onChange={(e) => updLine(l.key, { discountPctInput: e.target.value })}
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      disabled={!editable}
                      value={l.vatPctInput}
                      onChange={(e) => updLine(l.key, { vatPctInput: e.target.value })}
                      className={`${inputCls} text-right`}
                    />
                  </td>
                  <td className="py-1.5 text-center">
                    <input
                      type="checkbox"
                      disabled={!editable}
                      checked={l.isSupplied}
                      onChange={(e) => updLine(l.key, { isSupplied: e.target.checked })}
                    />
                  </td>
                  <td className="py-1.5 text-right font-mono t-secondary">
                    {formatCents(net + vat)}
                  </td>
                  {editable && (
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                        className="t-faint hover:text-rose-700 dark:hover:text-rose-300"
                        title={t('removeLine')}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Totales */}
        <div className="flex justify-end">
          <div className="text-sm space-y-1 min-w-[260px]">
            <div className="flex justify-between t-secondary">
              <span>{t('totals.base')}</span>
              <span className="font-mono">{formatCents(totals.subtotal)}</span>
            </div>
            <div className="flex justify-between t-secondary">
              <span>{t('totals.vat')}</span>
              <span className="font-mono">{formatCents(totals.vat)}</span>
            </div>
            {totals.irpf > 0 && (
              <div className="flex justify-between t-secondary">
                <span>{t('totals.irpf')}</span>
                <span className="font-mono">-{formatCents(totals.irpf)}</span>
              </div>
            )}
            <div className="flex justify-between t-primary font-semibold border-t border-base pt-1">
              <span>{t('totals.total')}</span>
              <span className="font-mono">{formatCents(totals.total)}</span>
            </div>

            {/* Margen interno (E6) — NUNCA visible para el cliente ni en el PDF */}
            <div className="mt-2 rounded-lg border border-amber-500/25 bg-amber-500/[0.04] p-2.5 text-xs">
              <div className="flex items-center justify-between mb-1 text-amber-700 dark:text-amber-300/80">
                <span className="uppercase tracking-wider text-[10px] font-semibold">
                  {t('totals.internalMargin')}
                </span>
                <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-700 dark:text-amber-300/70">
                  {t('totals.internal')}
                </span>
              </div>
              <div className="flex justify-between text-amber-700 dark:text-amber-200/80">
                <span>{t('totals.cost')}</span>
                <span className="font-mono">{formatCents(totals.totalCost)}</span>
              </div>
              <div className="flex justify-between text-amber-800 dark:text-amber-100/90 font-medium">
                <span>{t('totals.margin')}</span>
                <span className="font-mono">
                  {formatCents(totals.margin)}
                  {totals.subtotal > 0 && (
                    <span className="text-amber-700 dark:text-amber-300/60 ml-1">
                      ({((totals.margin / totals.subtotal) * 100).toFixed(0)}%)
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notas */}
      <div className="surface p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs t-muted mb-1">
            {t('conditions')}
          </label>
          <textarea
            disabled={!editable}
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('conditionsPlaceholder')}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs t-muted mb-1">{t('internalNotes')}</label>
          <textarea
            disabled={!editable}
            rows={3}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      {/* Plan de pagos del presupuesto */}
      {!isNew && detail && id && (
        <MilestonesPanel
          key={milestonesKey}
          clientId={detail.clientId}
          quoteId={id}
          canWrite
        />
      )}

      {/* Picker de tarifas */}
      <RatePickerModal
        open={ratePickerOpen}
        clientId={clientId || null}
        serviceLine={selectedCase?.serviceLine ?? null}
        onClose={() => setRatePickerOpen(false)}
        onPick={(item) => {
          setLines((ls) => {
            const next = ls.filter((l) => l.description.trim() || parseEurosToCents(l.unitPriceEur) !== 0)
            return [
              ...next,
              {
                key: lineKeySeq++,
                productId: null,
                description: item.name + (item.description ? ` — ${item.description}` : ''),
                quantity: '1',
                unitPriceEur: centsToEurosInput(item.appliedAmountCents),
                costEur: '',
                discountPctInput: '0',
                vatPctInput: (item.vatRate * 100).toString(),
                isSupplied: false,
              },
            ]
          })
          setRatePickerOpen(false)
        }}
      />

      {/* Modal de plan de pagos */}
      {!isNew && detail && id && (
        <PaymentPlanModal
          open={planOpen}
          onClose={() => setPlanOpen(false)}
          clientId={detail.clientId}
          opportunityId={detail.opportunityId}
          quoteId={id}
          subtotalCents={detail.subtotalCents}
          onCreated={() => setMilestonesKey((k) => k + 1)}
        />
      )}

      {/* Modal de envío por email */}
      <Modal open={sendOpen} onClose={() => setSendOpen(false)} title={t('sendModal.title')}>
        <div className="space-y-3">
          <div>
            <label className="block text-xs t-muted mb-1">{t('sendModal.recipient')}</label>
            <input
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              placeholder={clientEmail ?? t('sendModal.recipientPlaceholder')}
              className={inputCls}
            />
            {!clientEmail && !sendTo && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                {t('sendModal.noEmailWarning')}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">{t('sendModal.message')}</label>
            <textarea
              rows={3}
              value={sendMessage}
              onChange={(e) => setSendMessage(e.target.value)}
              placeholder={t('sendModal.messagePlaceholder')}
              className={inputCls}
            />
          </div>
          <p className="text-xs t-faint">
            {t('sendModal.footer')}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setSendOpen(false)}
              className="px-3 py-1.5 rounded-lg text-sm border border-base hover:bg-hover t-secondary"
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              disabled={actionBusy === 'SEND' || !sendTo.trim()}
              onClick={() => void onSend()}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5 disabled:opacity-50"
            >
              {actionBusy === 'SEND' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              {t('send')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
